import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '@/components/ui/button';
import { StripeService } from '@/services/stripeService';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCart } from '@/contexts/CartContext';
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const CartCheckoutForm = ({ photos, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();
    const { clearCart } = useCart();
    const { state } = useCart();
    const total = state.total;

  
    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return;
  
      setIsLoading(true);
      setErrorMessage('');
  
      try {
        // Confirm the payment with Stripe.js
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: `${window.location.origin}/payment-success`,
          },
        });
  
        if (error) {
          setErrorMessage(error.message || 'An unexpected error occurred');
          return;
        }
  
        if (paymentIntent.status === 'succeeded') {
          try {
            // Convert cart items with their discounted prices
            const cartItems = state.items.map(item => ({
              photo: {
                ...item.photo,
                // Use discounted price instead of original price
                price: item.discountedPrice !== undefined ? item.discountedPrice : item.photo.price
              },
              quantity: 1
            }));
  
            // Process the payment in our backend
            await StripeService.processCartPayment(paymentIntent.id, cartItems);
  
            // If we get here, everything was successful
            toast({
              title: "Success",
              description: "Your photos have been purchased successfully!",
            });
  
            // Clear the cart only after successful processing
            clearCart();
  
            // Call onSuccess if provided
            if (onSuccess) {
              onSuccess(paymentIntent.id);
            }
          } catch (processError) {
            console.error('Error processing payment:', processError);
            toast({
              variant: "destructive",
              title: "Processing Error",
              description: "Your payment was received but there was an error processing your purchase. Our team will assist you shortly.",
            });
            // Don't clear cart in case of processing error
          }
        } else if (paymentIntent.status === 'processing') {
          toast({
            title: "Payment Processing",
            description: "Your payment is being processed. We'll notify you when it's complete.",
          });
          // Start polling for status
          pollPaymentStatus(paymentIntent.id);
        } else {
          setErrorMessage(`Payment status: ${paymentIntent.status}. Please try again.`);
        }
      } catch (err) {
        console.error('Cart checkout error:', err);
        setErrorMessage(err.message || 'An error occurred during payment processing');
        toast({
          variant: "destructive",
          title: "Error",
          description: err.message || "Something went wrong with your payment",
        });
      } finally {
        setIsLoading(false);
      }
    };
  
    const pollPaymentStatus = async (paymentIntentId) => {
      let attempts = 0;
      const maxAttempts = 15;
      const interval = setInterval(async () => {
        try {
          const result = await StripeService.verifyPaymentStatus(paymentIntentId);
          
          if (result.paymentStatus === 'succeeded' && result.purchaseComplete) {
            clearInterval(interval);
            toast({
              title: "Success",
              description: "Your photos have been purchased successfully!",
            });
            clearCart();
            if (onSuccess) {
              onSuccess(paymentIntentId);
            }
          } else if (result.paymentStatus === 'canceled' || result.paymentStatus === 'failed') {
            clearInterval(interval);
            setErrorMessage(`Payment ${result.paymentStatus}. Please try again.`);
          }
          
          attempts++;
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            toast({
              variant: "warning",
              title: "Payment Status Pending",
              description: "We're still processing your payment. Please check your purchased photos shortly.",
            });
          }
        } catch (error) {
          console.error('Error polling payment status:', error);
          clearInterval(interval);
        }
      }, 2000);
    };
  
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        <PaymentElement />
        <Button
          type="submit"
          disabled={isLoading || !stripe || !elements}
          className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            `Pay $${total.toFixed(2)}`
          )}
        </Button>
      </form>
    );
  };
  
export const CartStripePayment = ({ photos, onSuccess, onError, disabled }) => {
    const [clientSecret, setClientSecret] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const { toast } = useToast();
    const [hasInitialized, setHasInitialized] = useState(false);
    const { state } = useCart();


    useEffect(() => {
      const initializePayment = async () => {
        if (hasInitialized || photos.length === 0) return;

        try {
          setIsLoading(true);
          setHasInitialized(true);
          // Calculate total from all photos with discounts
          const total = state.total;
          
          // Create payment intent using cart total (which includes discounts)
          const { clientSecret } = await StripeService.createCartPaymentIntent(photos, total);
          setClientSecret(clientSecret);
        } catch (err) {
          console.error('Failed to initialize cart payment:', err);
          toast({
            variant: 'destructive',
            title: 'Error',
            description: err.message || 'Failed to initialize payment'
          });
          if (onError) onError(err);
        } finally {
          setIsLoading(false);
        }
      };
  
      if (photos.length > 0) {
        initializePayment();
      }
    }, [photos, onError, toast]);
  
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      );
    }
  
    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#4f46e5',
              colorBackground: '#ffffff',
              colorText: '#1f2937',
              colorDanger: '#ef4444',
              spacingUnit: '4px',
              borderRadius: '6px',
            },
          },
        }}
      >
        <CartCheckoutForm photos={photos} onSuccess={onSuccess} />
      </Elements>
    );
  };
